import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { UseControlledMutation } from '@/_schemas/types';
import { apiClient, queryClient } from '@/_shared';

import { buildInvoiceEmailHistoryAdvanceFilter } from './invoice-email-history-filter';
import type { EmailHistoryQueryAllSchema, EmailHistoryQuerySchema } from './schemas';

export const EMAIL_HISTORIES_DEFAULT_SORT = '-latest_activity';

/** Required to match `email_metadata.to` to contacts and show profile images on recipients. */
export const EMAIL_HISTORIES_RECIPIENT_INCLUDES =
  'email_recipient_contacts.emails,email_recipient_contacts.profile_image';

/** List table: source, template, client/brand avatars, sender with profile image */
export const EMAIL_HISTORIES_LIST_INCLUDE = `source,email_template,brand.icon,client.logo,sender.profile_images,${EMAIL_HISTORIES_RECIPIENT_INCLUDES}`;

/** Profile detail: full relations for read-only form + view log */
export const EMAIL_HISTORIES_DETAIL_INCLUDE = `source,email_template,brand.icon,client.logo,sender.profile_images,${EMAIL_HISTORIES_RECIPIENT_INCLUDES}`;

/** Tooltip on invoice list: same recipient includes as list/detail. */
export const EMAIL_HISTORIES_INVOICE_TOOLTIP_INCLUDE = EMAIL_HISTORIES_LIST_INCLUDE;

export function buildEmailHistoriesForInvoiceApiParamStr(invoiceId: number, perPage = 20) {
  const advanceFilter = buildInvoiceEmailHistoryAdvanceFilter(invoiceId);
  return `?page=1&per_page=${perPage}&sort=${EMAIL_HISTORIES_DEFAULT_SORT}&filter[advance_filtering]=${encodeURIComponent(advanceFilter)}&include=${EMAIL_HISTORIES_INVOICE_TOOLTIP_INCLUDE}`;
}

export function useGetEmailHistoriesByInvoiceId(invoiceId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['EMAILS_HISTORY_BY_INVOICE', invoiceId],
    enabled: (options?.enabled ?? true) && invoiceId > 0,
    queryFn: async () => {
      const res = await apiClient.get<EmailHistoryQueryAllSchema>({
        endpoint: '/api/v1/email-histories',
        params: buildEmailHistoriesForInvoiceApiParamStr(invoiceId),
        preventAuditLog: true,
      });
      return res?.data ?? [];
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['EMAILS_HISTORY'] });
};

export const useGetEmailHistoryMutation: UseControlledMutation<EmailHistoryQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['EMAILS_HISTORY_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<EmailHistoryQueryAllSchema>({
            endpoint: '/api/v1/email-histories',
            params: { search, per_page, sort: EMAIL_HISTORIES_DEFAULT_SORT },
          });
          return res?.data ?? [];
        },
      });
    },
  });

export function useGetEmailsHistory(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['EMAILS_HISTORY', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<EmailHistoryQueryAllSchema>({
        endpoint: '/api/v1/email-histories',
        params: `${apiParamStr}&include=${EMAIL_HISTORIES_LIST_INCLUDE}`,
      });
      return res;
    },
  });
}

export function useGetEmailHistory(id: string) {
  return useSuspenseQuery({
    queryKey: ['EMAIL_HISTORY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: EmailHistoryQuerySchema }>({
        endpoint: `/api/v1/email-histories/${id}`,
        params: { include: EMAIL_HISTORIES_DETAIL_INCLUDE },
        preventAuditLog: true,
      });

      return res;
    },
  });
}
